import { GetStaticProps, InferGetStaticPropsType } from 'next'
import React, { ReactElement } from 'react'

import api from '@/services/api'
import createOgImage from '@/lib/createOgImage'

import scrollAnimation from '@/assets/animations/scroll.json'

import SEO from '@/components/SEO'
import Button from '@/components/Button'
import Swiper, { SwiperSlide } from '@/components/Swiper'

import { Container, Lottie } from '@/styles/pages/Proposal'

interface ProposalStaticProps {
  properties: {
    projectDeadline: {
      id: number
      deadline: string
    }[]
    projectTypes: {
      id: number
      title: string
    }[]
    projectScopes: {
      id: number
      title: string
    }[]
  }
  ogImage: string
}

type ProposalProps = InferGetStaticPropsType<
  GetStaticProps<ProposalStaticProps>
>

export default function Proposal({
  ogImage,
  properties
}: ProposalProps): ReactElement {
  function handleProposalSubmission(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    console.log(e.currentTarget.querySelectorAll('input'))
  }

  return (
    <Container onSubmit={handleProposalSubmission}>
      <SEO
        title="Proposal"
        description="Fazer proposta/contratar meu serviços"
        image={ogImage}
      />

      <Swiper
        direction={'vertical'}
        // mousewheelControl
        // controller
        mousewheel
        parallax
        spaceBetween={30}
        speed={1000}
        slidesPerView={1}
        pagination={{
          clickable: true
        }}
      >
        <SwiperSlide>
          <header>
            <div className="title-box">
              <h1>Proposta</h1>
              <span>
                Você deseja iniciar um projeto ou fazer melhorias? Não importa o
                tamanho da sua empresa, eu ajudo você. Segue abaixo umas
                perguntas para melhor entendimento da sua ideia.
              </span>
            </div>
          </header>
        </SwiperSlide>

        <SwiperSlide>
          <section className="personal-data">
            <h2>Formulário</h2>

            <div>
              <input placeholder="Seu nome" />
              <input placeholder="Seu email comercial" />
              <input placeholder="Seu Whatsapp" />
              <input placeholder="O nome da empresa" />
            </div>
          </section>
          <ScrollAnimation />
        </SwiperSlide>

        <SwiperSlide>
          <section className="project-type">
            <h3>Tipo de projeto</h3>

            <div className="checkboxes">
              {properties.projectTypes.map(type => (
                <div key={`${type.title}-${type.id}`} className="inputGroup">
                  <input
                    id={`${type.title}-${type.id}`}
                    name={String(type.id)}
                    type="checkbox"
                  />
                  <label htmlFor={`${type.title}-${type.id}`}>
                    {type.title}
                  </label>
                </div>
              ))}
            </div>
          </section>
          <ScrollAnimation />
        </SwiperSlide>

        <SwiperSlide>
          <section className="project-scopes">
            <h3>Escopo do projeto</h3>

            <div className="checkboxes">
              {properties.projectScopes.map(type => (
                <div key={`${type.title}-${type.id}`} className="inputGroup">
                  <input
                    id={`${type.title}-${type.id}`}
                    name={String(type.id)}
                    type="checkbox"
                  />
                  <label htmlFor={`${type.title}-${type.id}`}>
                    {type.title}
                  </label>
                </div>
              ))}
            </div>
          </section>
          <ScrollAnimation />
        </SwiperSlide>

        <SwiperSlide>
          <section className="project-deadline">
            <h3>Prazo do projeto</h3>

            <div className="checkboxes">
              {properties.projectDeadline.map(type => (
                <div key={`${type.deadline}-${type.id}`} className="inputGroup">
                  <input
                    id={`${type.deadline}-${type.id}`}
                    name={String(type.id)}
                    type="checkbox"
                  />
                  <label htmlFor={`${type.deadline}-${type.id}`}>
                    {type.deadline}
                  </label>
                </div>
              ))}
            </div>
          </section>

          <Button
            type="button"
            text="Converse comigo"
            onClick={() => {
              window.open('https://www.instagram.com/solrachix', '_blank')
            }}
          />
        </SwiperSlide>
      </Swiper>
    </Container>
  )
}

function ScrollAnimation() {
  const defaultAnimationOptions = {
    loop: true,
    autoplay: true,
    animationData: scrollAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  }

  return <Lottie options={defaultAnimationOptions} />
}

export const getStaticProps: GetStaticProps<ProposalStaticProps> = async () => {
  const ogImage = await createOgImage({
    title: 'Make a Proposal'
  })

  const emptyProperties = {
    projectDeadline: [],
    projectTypes: [],
    projectScopes: []
  }

  let properties = emptyProperties

  try {
    const { data } = await api.get('proposals/properties')
    properties = {
      projectDeadline: data?.projectDeadline ?? [],
      projectTypes: data?.projectTypes ?? [],
      projectScopes: data?.projectScopes ?? []
    }
  } catch (error) {
    console.error('Failed to fetch proposal properties:', error)
  }

  return {
    revalidate: 86400,
    props: {
      properties,
      ogImage: ogImage
    }
  }
}
